// WP-07 / WP-22 (D-F5) / AUDIT-FIX shared-contract (P1-01/P1-02) / WP-UX-1 —
// office-web tenant branding seam (CR-002; LLD-05 §2.6). The canonical
// branding contract (shape, Steier default palette, hex validator, merge
// resolver) AND the §10.1.1 colour guard live in @steier/shared/contract and
// are IMPORTED here — never re-declared. tokens.css carries the Steier default
// roles as generated CSS custom properties; the theme-tokens drift test
// regenerates them from the shared module. Two entry points:
//   - applyBranding()          — the org-settings live preview (owner, O-15).
//   - applyCanonicalBranding() — app-wide theming from the login response
//     org.branding (D-WP22-3: the owner-only GET /org/branding can't serve
//     non-owners; the login payload can). No new fetch.
// Both route the tenant's colours through `guardTenantColours` (UX v1.6
// §10.1.1): the primary becomes `identity` with a legible on-colour (AF-4 row
// 64a — banner text and Sign out stay readable on any primary), the accent is
// split into `brand.fill` (decorative fills, as typed) and `brand.accessible`
// (text/links/buttons — darkened until ≥ 4.5:1 on both surfaces). Status
// roles never pass through here. Never hardcode a tenant's branding.
import {
  colourRoleCssVar,
  guardTenantColours,
  isHexColour,
  resolveClientBranding,
  STEIER_DEFAULT_BRANDING,
  type GuardedPalette,
  type TenantBrandingConfig,
} from "@steier/shared/contract";

export { STEIER_DEFAULT_BRANDING };
/** The canonical organization.branding jsonb shape (LLD-01 §7.2, shared). */
export type CanonicalBranding = TenantBrandingConfig;

/**
 * Input of the O-15 org-settings live preview — the form's own field names,
 * NOT the canonical organization.branding jsonb. Renamed from `OrgBranding`
 * (P1-02: the old name collided with field's canonical-shaped `OrgBranding`
 * while carrying disjoint keys).
 */
export interface BrandingPreviewInput {
  name?: string;
  colourPrimary?: string;
  colourAccent?: string;
  logoUrl?: string;
}

/** CSS custom-property values are written lower-case (tokens.css convention). */
function css(hex: string): string {
  return hex.toLowerCase();
}

/**
 * Overwrite the CSS custom properties with the GUARDED tenant colours (else
 * leave the Steier defaults from tokens.css in place). Each colour is applied
 * only when the tenant supplied a valid hex for it, so an un-branded role
 * renders the literal §10.1 token rather than the guard's derivation of it.
 * The canonical `--colour-*` role vars and the legacy `--steier-*` aliases
 * are both set (D-WPUX-1-7). Returns the guard's verdict so O-15 can show the
 * "Adjusted for legibility" notice, or null when nothing was applied.
 */
export function applyBranding(branding: BrandingPreviewInput): GuardedPalette | null {
  const primary = isHexColour(branding.colourPrimary) ? branding.colourPrimary : undefined;
  const accent = isHexColour(branding.colourAccent) ? branding.colourAccent : undefined;
  if (!primary && !accent) return null;

  const palette = guardTenantColours({ primary, accent });
  const root = document.documentElement;
  const set = (name: string, value: string): void => root.style.setProperty(name, css(value));

  if (primary) {
    set(colourRoleCssVar("identity"), palette.identity);
    // AF-4 (row 64a): the banner's text (and Sign out) sit ON the primary, so
    // the on-colour follows the tenant's primary (white or ink), never a fixed white.
    set(colourRoleCssVar("on.identity"), palette.onIdentity);
    set("--steier-colour-primary", palette.identity);
    set("--steier-colour-text-inverse", palette.onIdentity);
  }
  if (accent) {
    set(colourRoleCssVar("brand.accessible"), palette.accessible);
    set(colourRoleCssVar("brand.accessible.hover"), palette.accessibleHover);
    set(colourRoleCssVar("brand.accessible.pressed"), palette.accessiblePressed);
    set(colourRoleCssVar("brand.fill"), palette.fill);
    set(colourRoleCssVar("brand.surface"), palette.brandSurface);
    // The legacy accent alias carried text/link/button colour → `accessible`, never `fill`.
    set("--steier-colour-accent", palette.accessible);
  }
  return palette;
}

/**
 * Apply a tenant's CANONICAL branding (from the login response) app-wide.
 * Invalid/absent colours keep the Steier default tokens — a half-configured
 * tenant never breaks the shell. Idempotent; safe to call on boot + on login.
 */
export function applyCanonicalBranding(branding: unknown): GuardedPalette | null {
  if (!branding || typeof branding !== "object") return null;
  const b = branding as TenantBrandingConfig;
  return applyBranding({
    colourPrimary: isHexColour(b.primaryColor) ? b.primaryColor : undefined,
    colourAccent: isHexColour(b.accentColor) ? b.accentColor : undefined,
  });
}

/** Display name for the sign-in card: preview name > slug > platform wordmark. */
export function displayName(branding: BrandingPreviewInput, slugHint?: string): string {
  return branding.name ?? slugHint ?? STEIER_DEFAULT_BRANDING.wordmark;
}

/**
 * Shell brand label from a canonical branding + the tenant's own org name:
 * emailDisplay > org name > slug > platform wordmark. Delegates to the shared
 * resolver (P1-02: one merge rule, not three).
 */
export function brandLabel(
  branding: unknown,
  orgName?: string | null,
  slugHint?: string,
): string {
  const nameFallback =
    [orgName, slugHint].find(
      (value): value is string => typeof value === "string" && value.trim().length > 0,
    ) ?? "";
  return resolveClientBranding(branding, nameFallback).displayName;
}
